import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ExternalApp, externalappsListApps } from '@column/api';
import { Button, Modal, ModalSize } from '@column/column-ui-kit';
import { Badge, cn } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { setAppsContextCookies } from '~/util';

import { APPS_REDIRECT_SECONDS } from './constants';
import { getExternalAppRedirectSlug, getExternalAppRedirectURL, getTargetMode } from './utils';

const MODAL_CLOSE_ANIMATION_MS = 250;

const modeBadgeClassName = (mode: 'live' | 'sandbox') =>
  cn(
    'translate-y-px rounded-md',
    mode === 'sandbox'
      ? 'border-orange-700/10 bg-orange-200/50 text-orange-600/80 dark:border-orange-400/20 dark:bg-orange-500/15 dark:text-orange-300'
      : 'border-seafoam-700/10 bg-seafoam-100/50 text-seafoam-700 dark:border-seafoam-400/20 dark:bg-seafoam-500/15 dark:text-seafoam-300'
  );

export const AppsRedirectModal: FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isExternalAppsEnabled = useFeatureFlag(FeatureFlag.EnableExternalApps);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<number | undefined>(undefined);
  const { currentPlatform, currentUser, isSandbox } = useSessionStore();
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const appRedirect = searchParams.get('app_redirect');
  const appRedirectSlug = getExternalAppRedirectSlug(appRedirect);
  const [isDismissed, setIsDismissed] = useState(false);
  const hasRedirect = isExternalAppsEnabled && location.pathname === ROUTE.ROOT && !!appRedirect;
  const platformId = currentPlatform?.id ?? currentUser?.defaultPlatformId;
  const canLoadApp = hasRedirect && !!platformId && !!appRedirectSlug;
  const appQuery = useQuery({
    queryKey: ['externalApps', platformId],
    queryFn: async () => {
      const { data } = await externalappsListApps({ throwOnError: true });
      return data?.apps ?? [];
    },
    enabled: canLoadApp,
    retry: false,
  });
  const redirectApp = appQuery.data?.find((app) => app.slug === appRedirectSlug);
  const targetMode = redirectApp ? getTargetMode(redirectApp.modes, isSandbox) : undefined;
  const [lastLoadedApp, setLastLoadedApp] = useState<ExternalApp>();
  const [lastTargetMode, setLastTargetMode] = useState<'live' | 'sandbox'>();
  const hasAppLoaded = !!redirectApp?.address && !!targetMode;
  const hasAppLoadError = canLoadApp && (appQuery.isError || (appQuery.isFetched && !hasAppLoaded));
  const isOpen = hasRedirect && hasAppLoaded && !hasAppLoadError && !isDismissed;
  const displayApp = redirectApp ?? lastLoadedApp;
  const displayMode = targetMode ?? lastTargetMode;
  const appName = displayApp?.display_name ?? 'app';

  useEffect(() => {
    if (redirectApp && targetMode) {
      setLastLoadedApp(redirectApp);
      setLastTargetMode(targetMode);
    }
  }, [redirectApp, targetMode]);

  useEffect(() => {
    setIsDismissed(false);
  }, [appRedirect]);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    },
    []
  );

  const closeModal = useCallback(() => {
    setIsDismissed(true);

    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = window.setTimeout(() => {
      navigate(ROUTE.ROOT, { replace: true });
    }, MODAL_CLOSE_ANIMATION_MS);
  }, [navigate]);

  const closeModalImmediately = useCallback(() => {
    setIsDismissed(true);

    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }

    navigate(ROUTE.ROOT, { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!modalContentRef.current?.contains(event.target as Node)) {
        closeModal();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [closeModal, isOpen]);

  const continueRedirect = useCallback(() => {
    const appURL = redirectApp ? getExternalAppRedirectURL(redirectApp, appRedirect) : undefined;
    if (!platformId || !appURL || !targetMode) {
      return;
    }

    setAppsContextCookies({ platformId, isSandbox: targetMode === 'sandbox' });
    window.location.assign(appURL);
    closeModalImmediately();
  }, [appRedirect, closeModalImmediately, platformId, redirectApp?.address, targetMode]);

  useEffect(() => {
    if (!hasRedirect) {
      return;
    }

    if (isDismissed) {
      return;
    }

    if (!canLoadApp) {
      return;
    }

    if (appQuery.isError) {
      return;
    }

    if (!platformId) {
      navigate(ROUTE.PROFILE, { replace: true });
      return;
    }

    if (!redirectApp?.address || !targetMode) {
      return;
    }

    const timeout = window.setTimeout(() => {
      continueRedirect();
    }, APPS_REDIRECT_SECONDS * 1000);

    return () => window.clearTimeout(timeout);
  }, [
    canLoadApp,
    appQuery.isError,
    continueRedirect,
    hasRedirect,
    isDismissed,
    navigate,
    platformId,
    redirectApp,
    targetMode,
  ]);

  useEffect(() => {
    if (!hasAppLoadError) {
      return;
    }

    addDangerNotification({ content: "We couldn't load this app. Please try again." });
    navigate(ROUTE.ROOT, { replace: true });
  }, [addDangerNotification, hasAppLoadError, navigate]);

  return (
    <Modal isOpen={isOpen} onClose={closeModal} size={ModalSize.Small}>
      <div ref={modalContentRef}>
        <Modal.IconCircle type="primary">
          <ExternalLink className="text-primary" />
        </Modal.IconCircle>
        <Modal.Content>
          <Modal.Headline fullWidth>Launching {appName}</Modal.Headline>
          {displayMode && (
            <div className="flex justify-center pb-3">
              <Badge variant="outline" className={modeBadgeClassName(displayMode)}>
                {displayMode === 'sandbox' ? 'Sandbox' : 'Live'}
              </Badge>
            </div>
          )}
          <Modal.Paragraph fullWidth>
            This app will open automatically in {APPS_REDIRECT_SECONDS} seconds.
          </Modal.Paragraph>
        </Modal.Content>
        <Modal.Footer className="justify-center!">
          <Button type="button" size="small" variant="muted" onClick={() => continueRedirect()}>
            Continue
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};
